import React, {
    useState, useCallback, useMemo, useRef, useLayoutEffect
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fp from 'lodash/fp';
import Scrollparent from 'scrollparent';

import ContextModal from '../gridcomponents/ContextModal';
import HoverIcon from '../gridcomponents/HoverIcon';
import MoreOptions from '../../common/Icons/More-Options.svg';
import MoreOptionsHighlighted from '../../common/Icons/More-Options-Highlighted.svg';

const createContextColumn = (onClick) => ({
    id: 'Grid/Virtual/Context',
    width: 30,
    title: '',
    renderCell: (row) => row.original.contextOptions && (
        <div
            role="none"
            onClick={ (e) => {
                e.nativeEvent.isContextButtonClick = true;
                e.stopPropagation();
                onClick(e, row.original);
            } }
            className={ classNames(
                row.gridProps.styles['icon-cell-wrapper'],
                'context-menu-button'
            ) }
        >
            <HoverIcon icon={ MoreOptions } hoverIcon={ MoreOptionsHighlighted } styles={ row.gridProps.styles } />
        </div>
    ),
    columnSpacing: 0,
    overflow: true,
    noPadding: true
});

const findScrollParents = (element) => {
    const scrollParent = Scrollparent(element);
    if (scrollParent && scrollParent !== element) {
        return [element, ...findScrollParents(scrollParent)];
    }
    return [element];
};

const initialState = { row: null };
const theadRowHeight = 32;
const contextModalWidth = 160;

const withContextModal = (Component) => {
    const WrappedComponent = (props) => {
        const [rowState, setRowState] = useState(initialState);
        const [xOffset, setXOffset] = useState(0);
        const [yOffset, setYOffset] = useState(0);
        const contextRef = useRef(null);
        const getContextRef = (ref) => { contextRef.current = ref; };

        const openControl = useCallback((e, row) => {
            if (row.contextOptions && contextRef && contextRef.current) {
            // contextRef is attached one div below the Grid Tbody
                const { left, top } = contextRef.current.getBoundingClientRect();
                const xOffset = e.currentTarget.offsetLeft + left - contextModalWidth;
                // yOffset includes a -32px offset for the table header row
                const yOffset = e.currentTarget.offsetTop + top - theadRowHeight;
                setRowState({ row });
                setXOffset(xOffset);
                setYOffset(yOffset);
            }
        }, []);

        const updateOffsets = useCallback(() => setRowState(initialState), []);

        useLayoutEffect(
            () => {
                const scrollParents = findScrollParents(contextRef.current);

                scrollParents.forEach((sp) => {
                    if (sp.addEventListener) {
                        sp.addEventListener('scroll', updateOffsets);
                    }
                });

                return () => scrollParents.forEach((s) => s.removeEventListener('scroll', updateOffsets));
            },
            [
                updateOffsets
            ]
        );

        const closeControl = useCallback((e) => {
            if (!e.isContextButtonClick) {
                setRowState(initialState);
            }
        }, []);

        const rows = fp.map(
            (row) => (props.getContextOptions
                ? fp.set('contextOptions', props.getContextOptions(row, props), row)
                : row)
        )(props.rows);

        const columns = useMemo(
            () => [
                ...props.columns,
                props.getContextOptions ? createContextColumn(openControl) : null
            ].filter((x) => x),
            [props.columns, props.getContextOptions, openControl]
        );

        const contextModal = rowState.row && (
            <div className={ props.styles['react-table-modal'] }>
                <ContextModal
                    closeControl={ closeControl }
                    row={ rowState.row }
                    options={ rowState.row.contextOptions }
                    styles={ props.styles }
                    xOffset={ xOffset }
                    yOffset={ yOffset }
                />
            </div>
        );

        return (
            <Component
                { ...props }
                rows={ rows }
                columns={ columns }
                contextmodal={ contextModal }
                getContextRef={ getContextRef }
            />
        );
    };

    WrappedComponent.propTypes = {
        getContextOptions: PropTypes.func,
        rows: PropTypes.arrayOf(PropTypes.shape({})),
        columns: PropTypes.arrayOf(PropTypes.shape({})),
        styles: PropTypes.shape({
            'react-table-modal': PropTypes.string
        }).isRequired
    };

    WrappedComponent.defaultProps = {
        getContextOptions: undefined,
        rows: [],
        columns: []
    };

    return WrappedComponent;
};

export default withContextModal;
